/* RESPONSIBLE TEAM: team-reporting */

import type {
  ChartSchema,
  ChartDefinition,
  Timezone,
  DateRange,
} from '@/features/report-snapshots/api/schema';
import {
  ChartSeriesDataPoint,
  getSeries,
} from '@/features/report-snapshots/components/charts/utils/chart-data';
import { getLabels } from '@/features/report-snapshots/components/charts/utils/chart-labels';
import { ChartSeries } from '@/features/report-snapshots/components/charts/utils/chart-data';
import { ChartType } from '@/features/report-snapshots/types';
import DefaultSeriesChartConfig from '@/features/report-snapshots/components/charts/default-series-chart-config';
import { I18n } from '@lingui/core';
import { setTargetLine } from '@/features/report-snapshots/components/charts/target-line';
import { SERIES_COLOR_PALETTE } from '@/features/report-snapshots/constants';

import {
  getColumnKeys,
  isDurationColumn,
  isPercentColumn,
} from '@/features/report-snapshots/utils/chart-data-helpers';
import {
  relativeValuesYAxisFormatter,
  yAxisFormatter,
  durationYAxisFormatter,
} from '@/features/report-snapshots/formatters/y-axis-formatter';
import { formatLegendLabels } from '@/features/report-snapshots/formatters/legend-labels';
import { makeDefaultDataLabelFormatter } from '@/features/report-snapshots/formatters/datalabel-formatter';
import { makeDefaultTooltipFormatter } from '@/features/report-snapshots/formatters/tooltip-formatter';
import { comparisonSeries } from '@/features/report-snapshots/utils/comparison-series';
import { getDurationTickInterval } from '@/features/report-snapshots/formatters/duration-tick-positioner';

interface SeriesChartConfig extends Highcharts.Options {
  series: Highcharts.SeriesOptionsType[];
}

interface ExtendedSeries {
  data?: ChartSeriesDataPoint[];
  colors?: string[];
  colorByPoint?: boolean;
  name?: string;
  dataLabels?: {
    enabled?: boolean;
    formatter?: (this: { y: number }) => string | null;
  };
  type?: string;
  [key: string]: unknown;
}

interface ExtendedYAxis {
  max?: number;
  labels?: {
    formatter?: (this: { value: number | string }) => string;
  };
  plotLines?: Highcharts.YAxisPlotLinesOptions[];
  tickPositioner?: (this: { min: number; max: number }) => number[];
  [key: string]: unknown;
}

interface ExtendedXAxis {
  categories?: string[];
  reversed?: boolean;
  labels?: {
    enabled?: boolean;
  };
  [key: string]: unknown;
}

interface HighchartsConfig {
  chart?: { type?: string; [key: string]: unknown };
  series?: ExtendedSeries[];
  yAxis?: ExtendedYAxis | ExtendedYAxis[];
  xAxis?: ExtendedXAxis;
  tooltip?: { formatter?: () => string; [key: string]: unknown };
  colors?: string[];
  legend?: { labelFormatter?: () => string; [key: string]: unknown };
  plotOptions?: Record<string, Record<string, unknown>>;
  [key: string]: unknown;
}

// Column, bar, line, area, combo
export class SeriesChartBuilder {
  private config: DefaultSeriesChartConfig | null = null;

  private hasData(series: ExtendedSeries): boolean {
    return Boolean(series.data && Array.isArray(series.data));
  }

  private isMultiSeriesChart() {
    const config = this.getConfig();

    return Boolean(config.series?.length && config.series.length > 1);
  }

  private getConfig(): HighchartsConfig {
    return this.config?.config as HighchartsConfig;
  }

  constructor(
    private schemas: ChartSchema[],
    private chartDefinition: ChartDefinition,
    private chartType: ChartType,
    private timezone: Timezone,
    private i18n: I18n,
  ) {}

  static build(
    schemas: ChartSchema[],
    chartDefinition: ChartDefinition,
    chartType: ChartType,
    timezone: Timezone,
    i18n: I18n,
    dateRange: DateRange,
  ) {
    const isTimeComparisonEnabled = chartDefinition.visualizationOptions.showTimeComparison;

    if (isTimeComparisonEnabled) {
      return this.buildTimeComparisonChart(
        schemas,
        chartDefinition,
        chartType,
        timezone,
        i18n,
        dateRange,
      );
    }

    const seriesChart = new SeriesChartBuilder(schemas, chartDefinition, chartType, timezone, i18n);

    const series = seriesChart.buildSeries();
    const labels = seriesChart.buildLabels(series);
    const config = seriesChart.buildConfig(series, labels);

    return config;
  }

  private static buildTimeComparisonChart(
    schemas: ChartSchema[],
    chartDefinition: ChartDefinition,
    chartType: ChartType,
    timezone: Timezone,
    i18n: I18n,
    dateRange: DateRange,
  ) {
    const [previousSeries, currentSeries] = schemas.map((schema) =>
      getSeries(schema, chartType, chartDefinition, timezone, i18n),
    );

    const isMultimetric = currentSeries.length > 1;

    const styledSeries = comparisonSeries(
      previousSeries as ChartSeries[],
      currentSeries as ChartSeries[],
      dateRange,
      timezone,
      i18n,
      isMultimetric,
    );
    const labels = getLabels(currentSeries as ChartSeries[], chartDefinition, timezone, i18n);
    const chart = new SeriesChartBuilder(schemas, chartDefinition, chartType, timezone, i18n);
    const config = chart.buildConfig(styledSeries, labels);

    return config;
  }

  private buildSeries(): ChartSeries[] {
    return getSeries(
      this.schemas[0],
      this.chartType,
      this.chartDefinition,
      this.timezone,
      this.i18n,
    ) as ChartSeries[];
  }

  private buildLabels(series: ChartSeries[]) {
    return getLabels(series, this.chartDefinition, this.timezone, this.i18n);
  }

  private isPercent() {
    const keys = getColumnKeys(this.schemas[0]);
    return keys.some((key) => isPercentColumn(this.schemas[0], key));
  }

  private isRelative() {
    return this.chartDefinition.visualizationOptions.showRelativeValues;
  }

  private chartTypeSupportsStacking() {
    return ['column', 'bar', 'area'].includes(this.chartType);
  }

  private isStacked() {
    return this.chartDefinition.stacked && this.chartTypeSupportsStacking();
  }

  private isDuration() {
    const keys = getColumnKeys(this.schemas[0]);
    return keys.some((key) => isDurationColumn(this.schemas[0], key));
  }

  private isSegmented() {
    return this.chartDefinition.segmentBy;
  }

  private hasTarget() {
    if (!this.chartDefinition.visualizationOptions.target?.value) {
      return false;
    }
    return true;
  }

  private isComboChart() {
    return this.chartDefinition.visualizationType === 'combo';
  }

  private setChartType() {
    const config = this.getConfig();
    if (config.chart) {
      config.chart.type = this.chartType;
    }
  }

  private getMinAndMaxValues() {
    let maxValue = 0;
    let minValue = 0;

    const config = this.getConfig();
    config.series?.forEach((series: ExtendedSeries) => {
      if (this.hasData(series) && series.data) {
        series.data.forEach((data: ChartSeriesDataPoint) => {
          if (data.y && data.y > maxValue) {
            maxValue = data.y;
          }
          if (data.y && data.y < minValue) {
            minValue = data.y;
          }
        });
      }
    });

    return { minValue, maxValue };
  }

  private setYAxisRanges() {
    const config = this.getConfig();

    if (this.hasTarget()) {
      let { maxValue } = this.getMinAndMaxValues();

      let targetValue = this.chartDefinition.visualizationOptions.target?.value ?? 0;

      if (this.isPercent()) {
        targetValue = targetValue / 100;
      }

      if (maxValue < targetValue) {
        maxValue = targetValue;
        if (config.yAxis) {
          (config.yAxis as ExtendedYAxis).max = maxValue;
        }
      }
    }

    if (this.isPercent()) {
      if (config.yAxis) {
        if (this.isStacked() && !this.isSegmented()) {
          (config.yAxis as ExtendedYAxis).max = 1;
        }
      }
    }
  }

  private setTooltipFormatter() {
    const config = this.getConfig();
    if (!config.tooltip) return;

    config.tooltip.formatter = makeDefaultTooltipFormatter(
      this.isRelative(),
      this.isPercent(),
      this.isDuration(),
      this.isMultiSeriesChart(),
      this.isComboChart(),
      this.isStacked(),
    );
  }

  private setYAxisFormatter() {
    const config = this.getConfig();
    if (!config.yAxis) {
      return;
    }

    const axes = Array.isArray(config.yAxis) ? config.yAxis : [config.yAxis];
    let formatter;

    if (this.isRelative()) {
      formatter = relativeValuesYAxisFormatter;
    } else if (this.isDuration()) {
      formatter = durationYAxisFormatter;
    } else {
      formatter = yAxisFormatter(this.isPercent());
    }

    for (const axis of axes) {
      if (axis.labels) {
        axis.labels.formatter = formatter;
      }
    }
  }

  private setDurationTickInterval() {
    if (!this.isDuration()) {
      return;
    }

    const config = this.getConfig();
    if (!config.yAxis) {
      return;
    }

    const { minValue, maxValue } = this.getMinAndMaxValues();
    const tickInterval = getDurationTickInterval(minValue, maxValue);

    const axes = Array.isArray(config.yAxis) ? config.yAxis : [config.yAxis];

    for (const axis of axes) {
      if (tickInterval !== undefined) {
        axis.tickInterval = tickInterval;
      }
    }
  }

  private isXAxisTemporal() {
    return this.chartDefinition.viewBy === 'time';
  }

  private setConsistentColors() {
    const config = this.getConfig();
    config.colors = SERIES_COLOR_PALETTE;
  }

  private setColorByPoint() {
    if (
      !this.isXAxisTemporal() &&
      !this.isSegmented() &&
      !this.isStacked() &&
      !this.isMultiSeriesChart() &&
      !['line', 'area'].includes(this.chartType)
    ) {
      const config = this.getConfig();
      config.series?.forEach((series: ExtendedSeries) => {
        series.colorByPoint = true;
      });
    }
  }

  private setStacking() {
    let stackingConfig = {};
    if (this.isStacked()) {
      stackingConfig = {
        stacking: this.isRelative() ? 'percent' : 'normal',
      };
    }

    const config = this.getConfig();
    if (config.plotOptions) {
      const existingPlotOptions = config.plotOptions[this.chartType] ?? {};
      config.plotOptions[this.chartType] = {
        ...existingPlotOptions,
        ...stackingConfig,
      };
    }
  }

  private showDataLabels() {
    const config = this.getConfig();

    const showLabels = this.chartDefinition.chartSeries.some(
      (chartSeries) => chartSeries.showDataLabels,
    );

    config.series?.forEach((series: ExtendedSeries) => {
      series.dataLabels = {
        enabled: showLabels,
        formatter: makeDefaultDataLabelFormatter(
          this.isRelative(),
          this.isPercent(),
          this.isDuration(),
        ),
      };
    });
  }

  private setTargetLine() {
    const unit = this.schemas[0].columnDefinitions[1]?.unit;

    if (this.hasTarget() && unit) {
      const config = this.getConfig();
      if (config.yAxis) {
        (config.yAxis as ExtendedYAxis).plotLines = setTargetLine(
          this.chartDefinition.visualizationOptions.target?.value ?? 0,
          unit,
        );
      }
    }
  }

  private setXAxisConfig(labels: string[]) {
    const config = this.getConfig();
    if (config.xAxis) {
      config.xAxis.categories = labels;
      config.xAxis.type = this.isXAxisTemporal() ? 'datetime' : 'category';

      if (this.isXAxisTemporal() || this.isMultiSeriesChart()) {
        config.xAxis.reversed = true;
      }

      if (this.isStacked() && !this.isSegmented()) {
        if (config.xAxis.labels) {
          config.xAxis.labels.enabled = false;
        }
      }
    }
  }

  private setYAxisConfig() {
    if (this.isStacked() && !this.isSegmented()) {
      const config = this.getConfig();
      if (config.yAxis) {
        (config.yAxis as ExtendedYAxis).visible = false;
        (config.yAxis as ExtendedYAxis).reversedStacks = true;
      }
    }
  }

  private setComboYAxis() {
    const config = this.getConfig();
    if (config.yAxis) {
      config.yAxis = [
        config.yAxis as ExtendedYAxis,
        {
          ...(config.yAxis as ExtendedYAxis),
          opposite: true,
        },
      ];
    }
  }

  private setLegendConfig() {
    const config = this.getConfig();

    config.legend = {
      ...config.legend,
      reversed: this.isXAxisTemporal() || (this.isStacked() && !this.isSegmented()),
      enabled: config.series?.length && config.series.length > 1,
      labelFormatter: formatLegendLabels,
    };
  }

  private buildConfig(series: ChartSeries[], labels: string[]): SeriesChartConfig {
    this.config = new DefaultSeriesChartConfig(this.chartType);
    const config = this.getConfig();
    config.series = series as ExtendedSeries[];

    this.setChartType();
    this.setXAxisConfig(labels);
    if (this.chartType === 'combo') {
      this.setComboYAxis();
    }
    this.setYAxisConfig();
    this.setYAxisRanges();
    this.setTooltipFormatter();

    this.setYAxisFormatter();
    this.setDurationTickInterval();

    this.setConsistentColors();
    this.setColorByPoint();
    this.setStacking();
    this.showDataLabels();
    this.setTargetLine();
    this.setLegendConfig();

    return config as unknown as SeriesChartConfig;
  }
}
